import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import { SupportModal } from '@/components/feedback/support-modal'
import type { PlanType } from '@/services/billing-api'
import { captureViewportScreenshot } from '@/lib/feedback/capture-viewport'
import { useSubscriptionContext } from './subscription-provider'

export type ApplicationContextValue<T> =
  | { status: 'active'; value: T }
  | { status: 'unknown' }
  | { status: 'not_applicable' }

export type ApplicationDiagnosticContext = {
  plan: ApplicationContextValue<PlanType>
  marketplace: ApplicationContextValue<string>
  dateRange: ApplicationContextValue<unknown>
  asin: ApplicationContextValue<string>
  lastFailedHttpStatus: ApplicationContextValue<number>
  routeHistory: ApplicationContextValue<string[]>
}

export type PageDiagnosticContext = Pick<
  ApplicationDiagnosticContext,
  'marketplace' | 'dateRange'
> & Partial<Pick<ApplicationDiagnosticContext, 'asin' | 'lastFailedHttpStatus'>>

export type SupportEntrySource = 'sidebar' | 'contextual' | 'root-error'
export type SupportIntent = 'problem' | 'how_to' | 'suggestion'

export type OpenSupportEntry = {
  source?: SupportEntrySource
  intent?: SupportIntent
  contextOverride?: Partial<Pick<
    ApplicationDiagnosticContext,
    'marketplace' | 'dateRange' | 'asin' | 'lastFailedHttpStatus'
  >>
}

export type SupportEntrySnapshot = Readonly<{
  capturedAt: string
  source: SupportEntrySource
  intent?: SupportIntent
  pathname: string
  routeHistory: readonly string[]
  plan: ApplicationContextValue<PlanType>
  marketplace: ApplicationContextValue<string>
  dateRange: ApplicationContextValue<unknown>
  asin: ApplicationContextValue<string>
  lastFailedHttpStatus: ApplicationContextValue<number>
}>

export type SupportModalContextValue = {
  openModal: (entry?: OpenSupportEntry) => Promise<void>
  applicationContext: ApplicationDiagnosticContext
  entrySnapshot: SupportEntrySnapshot | null
  recordRoute: (pathname: string) => void
  registerDiagnosticContext: (context: PageDiagnosticContext) => () => void
}

const SupportModalContext = createContext<SupportModalContextValue>({
  openModal: async () => {},
  applicationContext: {
    plan: { status: 'unknown' },
    marketplace: { status: 'not_applicable' },
    dateRange: { status: 'not_applicable' },
    asin: { status: 'not_applicable' },
    lastFailedHttpStatus: { status: 'not_applicable' },
    routeHistory: { status: 'active', value: [] },
  },
  entrySnapshot: null,
  recordRoute: () => {},
  registerDiagnosticContext: () => () => {},
})

export function useSupportModal() {
  return useContext(SupportModalContext)
}

function cloneSerializable<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value)) as T
}

function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) return value
  for (const nested of Object.values(value as Record<string, unknown>)) {
    deepFreeze(nested)
  }
  return Object.freeze(value)
}

function freezeContextValue<T>(value: ApplicationContextValue<T>): ApplicationContextValue<T> {
  return deepFreeze(cloneSerializable(value))
}

function normalizePathname(pathname: string): string {
  return pathname.split(/[?#]/, 1)[0] || '/'
}

export function SupportModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [pageContext, setPageContext] = useState<PageDiagnosticContext | null>(null)
  const [entrySnapshot, setEntrySnapshot] = useState<SupportEntrySnapshot | null>(null)
  const [routeHistory, setRouteHistory] = useState<string[]>([])
  const routeHistoryRef = useRef<string[]>([])
  const pathnameRef = useRef(typeof window === 'undefined' ? '/' : window.location.pathname)
  const pageRegistrationRef = useRef<symbol | null>(null)
  const captureRequestRef = useRef(0)
  const { plan } = useSubscriptionContext()
  const queryClient = useQueryClient()
  const queryClientRef = useRef<QueryClient>(queryClient)
  const screenshotCleanupTimeoutRef = useRef<number | null>(null)
  queryClientRef.current = queryClient

  const recordRoute = useCallback((pathname: string) => {
    const normalizedPathname = normalizePathname(pathname)
    pathnameRef.current = normalizedPathname
    const previous = routeHistoryRef.current
    if (previous[previous.length - 1] === normalizedPathname) return
    const next = [...previous, normalizedPathname].slice(-5)
    routeHistoryRef.current = next
    setRouteHistory(next)
  }, [])

  const registerDiagnosticContext = useCallback((context: PageDiagnosticContext) => {
    const registration = Symbol('diagnostic-context')
    pageRegistrationRef.current = registration
    setPageContext(context)

    return () => {
      if (pageRegistrationRef.current !== registration) return
      pageRegistrationRef.current = null
      setPageContext(null)
    }
  }, [])

  const openModal = useCallback(async (entry: OpenSupportEntry = {}) => {
    if (entry.source === 'root-error' && typeof window !== 'undefined') {
      recordRoute(window.location.pathname)
    }
    const pathname = pathnameRef.current
    if (pathname !== '/app' && !pathname.startsWith('/app/')) return
    const captureRequest = ++captureRequestRef.current

    const overrides = entry.contextOverride ?? {}
    const frozenSnapshot = deepFreeze<SupportEntrySnapshot>({
      capturedAt: new Date().toISOString(),
      source: entry.source ?? 'sidebar',
      ...(entry.intent ? { intent: entry.intent } : {}),
      pathname,
      routeHistory: [...routeHistoryRef.current],
      plan: freezeContextValue({ status: 'active', value: plan }),
      marketplace: freezeContextValue(
        overrides.marketplace ?? pageContext?.marketplace ?? { status: 'not_applicable' },
      ),
      dateRange: freezeContextValue(
        overrides.dateRange ?? pageContext?.dateRange ?? { status: 'not_applicable' },
      ),
      asin: freezeContextValue(
        overrides.asin ?? pageContext?.asin ?? { status: 'not_applicable' },
      ),
      lastFailedHttpStatus: freezeContextValue(
        overrides.lastFailedHttpStatus
          ?? pageContext?.lastFailedHttpStatus
          ?? { status: 'not_applicable' },
      ),
    })
    setEntrySnapshot(frozenSnapshot)

    if (screenshotCleanupTimeoutRef.current !== null) {
      window.clearTimeout(screenshotCleanupTimeoutRef.current)
      screenshotCleanupTimeoutRef.current = null
    }

    // Capture screenshot BEFORE any overlay/modal renders
    try {
      const dataUrl = await captureViewportScreenshot({
        isCancelled: () => captureRequestRef.current !== captureRequest,
      })
      if (dataUrl === null) return
      setScreenshot(dataUrl)
    } catch (err) {
      if (captureRequestRef.current !== captureRequest) return
      console.warn('[SupportModal] Screenshot capture failed:', err)
      setScreenshot(null)
    }

    if (captureRequestRef.current !== captureRequest) return
    setIsOpen(true)
  }, [pageContext, plan, recordRoute])

  const closeModal = useCallback(() => {
    setIsOpen(false)
    // Clear screenshot after close animation
    screenshotCleanupTimeoutRef.current = window.setTimeout(() => {
      setScreenshot(null)
      setEntrySnapshot(null)
      screenshotCleanupTimeoutRef.current = null
    }, 200)
  }, [])

  useEffect(
    () => () => {
      if (screenshotCleanupTimeoutRef.current !== null) {
        window.clearTimeout(screenshotCleanupTimeoutRef.current)
      }
    },
    [],
  )

  const applicationContext = useMemo<ApplicationDiagnosticContext>(() => ({
    plan: { status: 'active', value: plan },
    marketplace: pageContext?.marketplace ?? { status: 'not_applicable' },
    dateRange: pageContext?.dateRange ?? { status: 'not_applicable' },
    asin: pageContext?.asin ?? { status: 'not_applicable' },
    lastFailedHttpStatus:
      pageContext?.lastFailedHttpStatus ?? { status: 'not_applicable' },
    routeHistory: { status: 'active', value: routeHistory },
  }), [pageContext, plan, routeHistory])

  const value = useMemo(
    () => ({
      openModal,
      applicationContext,
      entrySnapshot,
      recordRoute,
      registerDiagnosticContext,
    }),
    [applicationContext, entrySnapshot, openModal, recordRoute, registerDiagnosticContext],
  )

  return (
    <SupportModalContext.Provider value={value}>
      {children}
      <SupportModal
        open={isOpen}
        screenshot={screenshot}
        queryClient={queryClientRef.current}
        onClose={closeModal}
      />
    </SupportModalContext.Provider>
  )
}
